import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Loader2, Trash2, Upload } from 'lucide-react';
import { Card } from '@/components/ui';
import { useMatters } from '@/contexts/MatterContext';
import { ApiError } from '@/lib/api';
import {
  createMatterKnowledge,
  deleteMatterKnowledge,
  listMatterKnowledge,
  listRecentImports,
  type MatterKnowledgeEntrySummary,
  type RecentImport,
} from '@/lib/api-client';

// Section F.5 + F.13: paste a Claude conversation export (or any note) into a
// matter's knowledge base, then see what is already loaded. One-way ingestion:
// Court Copilot reads this into the ask flow; it never writes back to Claude.

const SOURCE_TYPES = [
  { value: 'CLAUDE_CONVERSATION_EXPORT', label: 'Claude conversation export' },
  { value: 'MANUAL_NOTE', label: 'Manual note' },
  { value: 'PROJECT_KNOWLEDGE', label: 'Project knowledge' },
  { value: 'DROPBOX_DOC_SUMMARY', label: 'Dropbox doc summary' },
  { value: 'EMAIL_THREAD_SUMMARY', label: 'Email thread summary' },
];
const MAX_CHARS = 200_000;

const inputClass =
  'w-full rounded-md border border-stone-800 bg-stone-900 px-3 py-2 text-sm text-stone-200 placeholder:text-stone-600 focus:border-amber-700/60 focus:outline-none';
const labelClass = 'mb-1 block text-[10px] uppercase tracking-[0.2em] text-stone-500';

export function AdminImport() {
  const { matters, activeMatterId } = useMatters();
  const [matterId, setMatterId] = useState<string>(activeMatterId ?? '');
  const [content, setContent] = useState('');
  const [title, setTitle] = useState('');
  const [sourceType, setSourceType] = useState('CLAUDE_CONVERSATION_EXPORT');
  const [tagsInput, setTagsInput] = useState('');
  const [entries, setEntries] = useState<MatterKnowledgeEntrySummary[]>([]);
  const [recent, setRecent] = useState<RecentImport[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Default to the active matter (or the first) once matters load.
  useEffect(() => {
    if (!matterId && matters.length > 0) {
      setMatterId(activeMatterId ?? matters[0]?.id ?? '');
    }
  }, [matters, activeMatterId, matterId]);

  // Load entries for the selected matter.
  useEffect(() => {
    if (!matterId) {
      setEntries([]);
      return;
    }
    let cancelled = false;
    listMatterKnowledge(matterId)
      .then((e) => {
        if (!cancelled) {
          setEntries(e);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setEntries([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [matterId]);

  // Load the cross-matter recent list on mount.
  useEffect(() => {
    listRecentImports()
      .then(setRecent)
      .catch(() => setRecent([]));
  }, []);

  const suggestedTitle = useMemo(() => {
    const line =
      content
        .split('\n')
        .map((l) => l.trim())
        .find((l) => l.length > 0) ?? '';
    return line.slice(0, 120);
  }, [content]);

  async function refresh(): Promise<void> {
    if (matterId) {
      setEntries(await listMatterKnowledge(matterId).catch(() => []));
    }
    setRecent(await listRecentImports().catch(() => []));
  }

  async function handleImport(): Promise<void> {
    setError(null);
    setNotice(null);
    const finalTitle = (title.trim() || suggestedTitle).slice(0, 300);
    if (!matterId) {
      setError('Select a matter.');
      return;
    }
    if (!content.trim()) {
      setError('Paste some content to import.');
      return;
    }
    if (content.length > MAX_CHARS) {
      setError(`Content is too long (${content.length} chars, max ${MAX_CHARS}).`);
      return;
    }
    if (!finalTitle) {
      setError('Add a title.');
      return;
    }
    const tags = tagsInput
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean)
      .slice(0, 40);
    setBusy(true);
    try {
      await createMatterKnowledge({
        matterId,
        title: finalTitle,
        content: content.trim(),
        sourceType,
        tags,
      });
      setNotice('Imported. It will prime every future query for this matter.');
      setContent('');
      setTitle('');
      setTagsInput('');
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Import failed.');
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(id: string): Promise<void> {
    setError(null);
    try {
      await deleteMatterKnowledge(id);
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  }

  return (
    <div className="min-h-screen bg-stone-950 px-5 py-6 sm:px-6">
      <div className="mx-auto w-full max-w-3xl space-y-5">
        <div className="flex items-center justify-between">
          <h1 className="font-display text-3xl italic text-stone-100">Import matter knowledge</h1>
          <Link
            to="/hearing"
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-stone-500 hover:text-amber-400"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Hearing
          </Link>
        </div>
        <p className="text-sm text-stone-400">
          Paste a Claude conversation export or any note. It becomes background context for every
          future query on the chosen matter. One way only: Court Copilot reads this, it never writes
          back to Claude.
        </p>

        {/* Import form (the amber-tinted special panel) */}
        <Card
          variant="default"
          padding="md"
          className="space-y-4 border-amber-900/30 bg-amber-950/10"
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className={labelClass} htmlFor="ak-matter">
                Matter
              </label>
              <select
                id="ak-matter"
                value={matterId}
                onChange={(e) => setMatterId(e.target.value)}
                className={inputClass}
              >
                {matters.length === 0 ? <option value="">No matters</option> : null}
                {matters.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass} htmlFor="ak-source">
                Source type
              </label>
              <select
                id="ak-source"
                value={sourceType}
                onChange={(e) => setSourceType(e.target.value)}
                className={inputClass}
              >
                {SOURCE_TYPES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className={labelClass} htmlFor="ak-title">
              Title
            </label>
            <input
              id="ak-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={suggestedTitle || 'Auto-filled from the first line'}
              className={inputClass}
            />
          </div>

          <div>
            <label className={labelClass} htmlFor="ak-tags">
              Tags (comma separated)
            </label>
            <input
              id="ak-tags"
              type="text"
              value={tagsInput}
              onChange={(e) => setTagsInput(e.target.value)}
              placeholder="strategy, evidence-plan, opposing-counsel-profile"
              className={inputClass}
            />
          </div>

          <div>
            <div className="mb-1 flex items-center justify-between">
              <label className={labelClass} htmlFor="ak-content">
                Content
              </label>
              <span
                className={
                  content.length > MAX_CHARS
                    ? 'text-[10px] text-red-400'
                    : 'text-[10px] text-stone-600'
                }
              >
                {content.length.toLocaleString()} / {MAX_CHARS.toLocaleString()}
              </span>
            </div>
            <textarea
              id="ak-content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Paste the conversation export or note here"
              rows={12}
              className={`${inputClass} resize-y font-mono text-[13px] leading-relaxed`}
            />
          </div>

          {error ? <p className="text-sm text-red-400">{error}</p> : null}
          {notice ? <p className="text-sm text-emerald-400">{notice}</p> : null}

          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => void handleImport()}
              disabled={busy || matters.length === 0}
              className="inline-flex items-center gap-2 rounded-md bg-amber-600 px-4 py-2 text-sm font-medium text-stone-950 transition-colors hover:bg-amber-700 disabled:opacity-40"
            >
              {busy ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              ) : (
                <Upload className="h-4 w-4" aria-hidden />
              )}
              Import
            </button>
          </div>
        </Card>

        {/* Entries for the selected matter */}
        <section className="space-y-2">
          <p className="text-[10px] uppercase tracking-[0.2em] text-stone-500">
            Loaded for this matter ({entries.length})
          </p>
          {entries.length === 0 ? (
            <p className="text-sm text-stone-500">Nothing imported yet for this matter.</p>
          ) : (
            <div className="space-y-2">
              {entries.map((e) => (
                <EntryRow key={e.id} entry={e} onDelete={() => void handleDelete(e.id)} />
              ))}
            </div>
          )}
        </section>

        {/* Recent imports across all matters (F.13) */}
        <section className="space-y-2">
          <p className="text-[10px] uppercase tracking-[0.2em] text-stone-500">
            Recent imports across all matters
          </p>
          {recent.length === 0 ? (
            <p className="text-sm text-stone-500">No imports yet.</p>
          ) : (
            <div className="space-y-1.5">
              {recent.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => setMatterId(r.matterId)}
                  className="flex w-full items-center justify-between gap-3 rounded-md border border-stone-800/60 bg-stone-900/60 px-3 py-2 text-left transition-colors hover:border-stone-700"
                >
                  <span className="min-w-0">
                    <span className="block truncate text-sm text-stone-200">{r.title}</span>
                    <span className="block text-[10px] uppercase tracking-[0.15em] text-stone-500">
                      {r.matterName} · {sourceLabel(r.sourceType)}
                    </span>
                  </span>
                  <span className="shrink-0 text-[10px] text-stone-600">{formatDate(r.createdAt)}</span>
                </button>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function EntryRow({
  entry,
  onDelete,
}: {
  entry: MatterKnowledgeEntrySummary;
  onDelete: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-md border border-stone-800/60 bg-stone-900/60 px-3 py-2">
      <div className="min-w-0">
        <p className="truncate text-sm text-stone-200">{entry.title}</p>
        <p className="text-[10px] uppercase tracking-[0.15em] text-stone-500">
          {sourceLabel(entry.sourceType)} · {entry.charCount.toLocaleString()} chars ·{' '}
          {formatDate(entry.createdAt)}
        </p>
      </div>
      <button
        type="button"
        onClick={onDelete}
        aria-label="Delete entry"
        className="shrink-0 rounded p-1 text-stone-500 transition-colors hover:text-red-400"
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  );
}

function sourceLabel(value: string): string {
  return SOURCE_TYPES.find((s) => s.value === value)?.label ?? value;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString([], { day: '2-digit', month: 'short', year: '2-digit' });
  } catch {
    return iso;
  }
}
